import { useEffect, useState } from "react";
import { Check, CheckCircle2, Fingerprint, LoaderCircle, Pencil, Plus, Trash2, X } from "lucide-react";
import { authClient } from "../../../lib/auth-client";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { InlineMessage } from "../../../components/konto-helpers";

type PasskeyItem = {
  id: string;
  name?: string | null;
  createdAt: string | Date;
  backedUp?: boolean;
};

export function SikkerhetTab() {
  const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchPasskeys();
  }, []);

  const fetchPasskeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await (authClient.passkey as any).listUserPasskeys?.();
      if (result?.error) {
        setError("Vi klarte ikke å hente passkeys akkurat nå.");
        setPasskeys([]);
      } else {
        setPasskeys(result?.data ?? []);
      }
    } catch {
      setError("Vi klarte ikke å hente passkeys akkurat nå.");
      setPasskeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    setAdding(true);
    setError(null);
    setMessage(null);
    try {
      const result = await authClient.passkey.addPasskey();
      if (result.error) {
        if (result.error.message !== "registration cancelled") {
          setError("Vi klarte ikke å opprette passkey.");
        }
      } else {
        setMessage("Ny passkey er lagt til.");
        await fetchPasskeys();
      }
    } catch {
      setError("Vi klarte ikke å opprette passkey.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError(null);
    setMessage(null);
    try {
      const result = await (authClient.passkey as any).deletePasskey?.({ id });
      if (result?.error) {
        setError("Vi klarte ikke å slette passkey.");
      } else {
        setPasskeys((current) => current.filter((p) => p.id !== id));
        setMessage("Passkey er slettet.");
      }
    } catch {
      setError("Vi klarte ikke å slette passkey.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRename = async (id: string) => {
    if (!editName.trim()) {
      setError("Gi passkeyen et navn før du lagrer.");
      return;
    }
    setError(null);
    setMessage(null);
    try {
      const result = await (authClient.passkey as any).updatePasskey?.({ id, name: editName.trim() });
      if (result?.error) {
        setError("Vi klarte ikke å oppdatere navnet.");
        return;
      }
      setPasskeys((current) =>
        current.map((p) => (p.id === id ? { ...p, name: editName.trim() } : p)),
      );
      setEditingId(null);
      setEditName("");
      setMessage("Passkey-navnet er oppdatert.");
    } catch {
      setError("Vi klarte ikke å oppdatere navnet.");
    }
  };

  return (
    <section
      className="rounded-3xl bg-white/50 p-8 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ border: "1px dashed var(--border)" }}
    >
      <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-3">
          <h2
            className="text-2xl font-black text-[#1a2e35]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Passkeys
          </h2>
          <p className="text-base font-medium text-muted-foreground max-w-md leading-relaxed">
            Bruk passkey for å logge inn med Face ID, Touch ID eller sikkerhetsnøkkel – sikrere og raskere enn passord.
          </p>
        </div>
        <Button
          variant="default"
          size="lg"
          onClick={handleAdd}
          disabled={adding}
          className="rounded-xl shadow-lg shadow-accent/20 font-bold transition-all hover:scale-105 active:scale-95"
        >
          {adding ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Ny passkey
        </Button>
      </div>

      <div className="space-y-4">
        <div
          className="rounded-lg px-4 py-3 text-sm"
          style={{ border: "1px solid var(--border)", background: "var(--muted)", color: "var(--muted-foreground)" }}
        >
          {loading ? (
            <span className="inline-flex items-center">
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              Henter passkeys...
            </span>
          ) : passkeys.length > 0 ? (
            <span className="inline-flex items-center">
              <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
              {passkeys.length} passkey registrert{passkeys.length === 1 ? "" : "e"}
            </span>
          ) : (
            <span className="inline-flex items-center">
              <Fingerprint className="mr-2 h-4 w-4 text-accent" />
              Ingen passkeys registrert ennå
            </span>
          )}
        </div>

        {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}
        {message ? <InlineMessage tone="success">{message}</InlineMessage> : null}

        {!loading && passkeys.length === 0 ? (
          <div
            className="rounded-xl border border-dashed px-6 py-10 text-center"
            style={{ borderColor: "var(--border)", background: "var(--muted)" }}
          >
            <Fingerprint className="mx-auto h-10 w-10 text-primary/35" />
            <p className="mt-4 text-base font-semibold" style={{ color: "var(--foreground)" }}>Ingen passkeys ennå</p>
            <p className="mt-2 text-sm" style={{ color: "var(--muted-foreground)" }}>
              Legg til minst én passkey for å gjøre innloggingen raskere og tryggere.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {passkeys.map((passkey) => (
              <div
                key={passkey.id}
                className="flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-border transition-all hover:shadow-md sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                    <Fingerprint className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    {editingId === passkey.id ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-10 min-w-[220px]"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => void handleRename(passkey.id)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingId(null); setEditName(""); }}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-muted-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="truncate text-sm font-semibold text-foreground">
                          {passkey.name || "Passkey"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Lagt til{" "}
                          {new Date(passkey.createdAt).toLocaleDateString("nb-NO", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                          {passkey.backedUp ? " · Synkronisert" : ""}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {editingId !== passkey.id && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setEditingId(passkey.id); setEditName(passkey.name ?? ""); }}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-muted-foreground transition hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(passkey.id)}
                      disabled={deletingId === passkey.id}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-destructive/20 bg-destructive/5 text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
                    >
                      {deletingId === passkey.id ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
